import fileDownload from 'js-file-download';
import { NETWORK_REQUEST_SENT } from './types';
import { BACKEND_URL_PREFIX } from '../utils/const';
import { buildDefaultHeaders } from './helper';
import { checkAndUpdateToken } from './auth';
import { createNotification } from '../components/notifications/notifications';

/* Режим «Кафедры» (Справочники) — двенадцать методов на чтение под /cathedras.
   Изменяющих методов у режима нет: кафедры и их вкладки заводятся в десктопе.

   Данные режима — экранное состояние одной открытой карточки, в Redux не
   складываются (как «Факультеты»): здесь только сам запрос, результат
   возвращается вызывающему компоненту.

   Внимание: GET /cathedra (единственное число) — общий справочник для полей
   отбора других режимов, он живёт в actions/cathedra.js и здесь не дублируется. */

/* Адрес собирается при вызове, а не один раз при загрузке модуля:
   utils/const.js импортирует PATHS из Routes.js, Routes.js — сам режим,
   режим — этот файл, и на этом круге BACKEND_URL_PREFIX ещё undefined. */
const base = () => `${BACKEND_URL_PREFIX}/cathedras`;

const buildQuery = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') search.set(key, value);
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

// Конверт ошибки API «Кафедр»: { error } либо { message }. Текст пригоден для показа.
const errorOf = async (response) => {
  try {
    const result = await response.json();
    if (result && (result.error || result.message)) return result.error || result.message;
  } catch (e) {
    /* тело не разобралось — остаётся запасной текст */
  }
  if (response.status === 403) return 'Отсутствуют права доступа';
  if (response.status === 404) return 'Кафедра не найдена';
  return 'Не удалось получить данные';
};

// Успех — { data }, отказ — { error, status }: код нужен вызывающему, чтобы
// отличить 403 (скрыть вкладку «Дисциплины» / «МТС») от прочих ошибок.
const request = (url) => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return { error: 'Сессия завершена', status: 401 };

  const response = await fetch(url, buildDefaultHeaders(getState));
  dispatch({ type: NETWORK_REQUEST_SENT, response });

  if (response.ok) return { data: await response.json() };
  return { error: await errorOf(response), status: response.status };
};

// Список. Отбор, поиск, сортировка и постраничная выдача — на сервере.
export const getCathedras = ({ search, status, parentId, sort, order, limit, offset }) =>
  request(`${base()}${buildQuery({
    search,
    status,
    parent_id: parentId,
    sort,
    order,
    limit,
    offset,
  })}`);

// Значения полей отбора «Статус» и «Вышестоящее подразделение» — с сервера,
// а не собранные по загруженной странице.
export const getCathedrasFilters = () => request(`${base()}/filters`);

export const getCathedra = (id) => request(`${base()}/${id}`);

export const getCathedraDepartments = (id) => request(`${base()}/${id}/departments`);

export const getCathedraSpecialties = (id) => request(`${base()}/${id}/specialties`);

export const getCathedraDisciplines = (id) => request(`${base()}/${id}/disciplines`);

// Без year сервер отдаёт текущий учебный год и перечень доступных лет.
export const getCathedraAdditionalWork = (id, year) =>
  request(`${base()}/${id}/additional_work${buildQuery({ year })}`);

// Календарный год (не учебный). Без year — последний введённый.
export const getCathedraRetraining = (id, year) =>
  request(`${base()}/${id}/retraining${buildQuery({ year })}`);

export const getCathedraPersonnel = (id) => request(`${base()}/${id}/personnel`);

export const getCathedraEquipment = (id) => request(`${base()}/${id}/equipment`);

export const getCathedraFiles = (id) => request(`${base()}/${id}/files`);

// Содержимое файла — application/octet-stream, тип по расширению.
// Просмотр в браузере или сохранение решает браузер; здесь просто сохраняем.
export const openCathedraFile = (id, fileId, name = 'file') => async (dispatch, getState) => {
  await dispatch(checkAndUpdateToken());
  if (!getState().auth.token) return;

  const response = await fetch(
    `${base()}/${id}/files/${fileId}`,
    buildDefaultHeaders(getState)
  );

  if (!response.ok) {
    createNotification('error', 'Не удалось получить файл', 'Ошибка');
    return;
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'noopener');
  if (!win) fileDownload(blob, name);
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};
